// src/components/ExercisePicker.js
import { useEffect, useMemo, useRef, useState } from "react";
import { FiX as XIcon } from "react-icons/fi";
import { api } from "../api";
import ExerciseListPanel from "./ExerciseListPanel";
import RollingPicker from "./RollingPicker";
import "./ExercisePicker.css";

function pickValue(v) {
  if (v == null) return "";
  if (typeof v === "object" && v.target && v.target.value != null) return v.target.value;
  if (typeof v === "object" && v.value != null) return v.value;
  return v;
}
function toInt(v, fallback) {
  const raw = pickValue(v);
  const n = parseInt(String(raw), 10);
  return Number.isFinite(n) ? n : fallback;
}
function toFloat(v, fallback) {
  const raw = pickValue(v);
  const n = parseFloat(String(raw));
  return Number.isFinite(n) ? n : fallback;
}

function splitRestSeconds(restSeconds, fallbackSeconds = 120) {
  const s0 = Number(restSeconds);
  const s = Number.isFinite(s0) && s0 >= 0 ? s0 : fallbackSeconds;
  const min = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return { min, sec };
}

export default function ExercisePicker({
  onClose,
  onSave,
  busy = false,
  mode = "add", // "add" | "edit"
  initialExercise = null,
  initialSets = 3,
  initialReps = 8,
  initialWeight = 20,
  initialRestSeconds = 120,
  itemId = null,
}) {
  const [view, setView] = useState(mode === "edit" ? "details" : "exercise");

  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");
  const [exercises, setExercises] = useState([]);
  const [muscleGroups, setMuscleGroups] = useState([]);

  const [filterIdx, setFilterIdx] = useState(0);
  const [selectedExerciseId, setSelectedExerciseId] = useState(null);

  const [selectedExerciseOverride, setSelectedExerciseOverride] = useState(
    initialExercise ? { id: initialExercise.id, name: initialExercise.name } : null
  );

  const [setsValue, setSetsValue] = useState(3);
  const [repsValue, setRepsValue] = useState(8);
  const [kgValue, setKgValue] = useState(20);

  const [restMinValue, setRestMinValue] = useState(2);
  const [restSecValue, setRestSecValue] = useState(0);

  useEffect(() => {
    if (mode !== "edit") return;

    if (initialExercise?.id) setSelectedExerciseId(initialExercise.id);
    if (initialExercise?.id && initialExercise?.name) {
      setSelectedExerciseOverride({ id: initialExercise.id, name: initialExercise.name });
    }

    setSetsValue(initialSets);
    setRepsValue(initialReps);
    setKgValue(initialWeight);

    const { min, sec } = splitRestSeconds(initialRestSeconds, 120);
    setRestMinValue(min);
    setRestSecValue(sec);
  }, [
    mode,
    initialExercise?.id,
    initialExercise?.name,
    initialSets,
    initialReps,
    initialWeight,
    initialRestSeconds,
  ]);

  const didLoadRef = useRef(false);

  const selectedExercise = useMemo(() => {
    if (selectedExerciseOverride) return selectedExerciseOverride;
    return (exercises || []).find((e) => e.id === selectedExerciseId) || null;
  }, [exercises, selectedExerciseId, selectedExerciseOverride]);

  useEffect(() => {
    if (view !== "exercise" && mode !== "edit") return;

    let cancelled = false;

    async function load() {
      if (didLoadRef.current) return;

      setLoading(true);
      setLoadErr("");

      try {
        const all = await api("/exercises", { cacheTtlMs: 6 * 60 * 60_000 });
        if (cancelled) return;

        const ex = Array.isArray(all) ? all : [];
        setExercises(ex);

        try {
          const mgRows = await api("/muscle-groups", { cacheTtlMs: 6 * 60 * 60_000 });
          if (cancelled) return;

          const groups = (Array.isArray(mgRows) ? mgRows : [])
            .map((r) => String(r?.muscle_group ?? r?.muscleGroup ?? "").trim())
            .filter(Boolean);

          setMuscleGroups(groups);
        } catch {
          const derived = Array.from(
            new Set(
              ex
                .map((e) => e?.muscleGroup?.group)
                .map((s) => (s ? String(s).trim() : ""))
                .filter(Boolean)
            )
          ).sort();
          setMuscleGroups(derived);
        }

        didLoadRef.current = true;
      } catch (err) {
        console.error("ExercisePicker: failed to load exercises", err);
        if (!cancelled) setLoadErr(err?.message || "Failed to load exercises.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [view, mode]);

  const bindIntPicker = (setter, fallback) => {
    const handler = (v) => setter(toInt(v, fallback));
    return { onChange: handler, onValueChange: handler };
  };
  const bindFloatPicker = (setter, fallback) => {
    const handler = (v) => setter(toFloat(v, fallback));
    return { onChange: handler, onValueChange: handler };
  };

  function goToRestStep() {
    if (!selectedExercise) return;
    setView("rest");
  }

  async function handleSaveAll() {
    if (!selectedExercise || busy) return;

    const sets = Math.max(1, toInt(setsValue, 3));
    const reps = Math.max(1, toInt(repsValue, 8));
    const weight = Math.max(0, toFloat(kgValue, 0));

    const m = Math.max(0, toInt(restMinValue, 2));
    const s = Math.max(0, Math.min(59, toInt(restSecValue, 0)));
    const restSeconds = m * 60 + s;

    await onSave?.({
      type: "exercise",
      exercise: selectedExercise,
      sets,
      reps,
      weight,
      restSeconds,
      itemId: itemId || null,
    });

    onClose?.();
  }

  const setsOptions = useMemo(() => Array.from({ length: 15 }, (_, i) => String(i + 1)), []);
  const repsOptions = useMemo(() => Array.from({ length: 60 }, (_, i) => String(i + 1)), []);
  const kgOptions = useMemo(() => Array.from({ length: 401 }, (_, i) => String(i)), []);

  const restMinOptions = useMemo(() => Array.from({ length: 16 }, (_, i) => String(i)), []);
  const restSecOptions = useMemo(() => Array.from({ length: 60 }, (_, i) => String(i)), []);

  return (
    <div
      className={[
        "exercise-picker",
        mode !== "edit" ? "glass" : "exercise-picker--plain",
        view !== "menu" ? "is-expanded" : "",
      ].join(" ")}
    >
      <button
        type="button"
        className="exercise-picker-close"
        aria-label="Close"
        title="Close"
        onClick={onClose}
      >
        <XIcon />
      </button>

      {view === "exercise" ? (
        <>
          <div className="exercise-picker-title">Choose an exercise</div>
          <div className="exercise-picker-body">
            {loading ? (
              <div className="exercise-picker-loading">Loading…</div>
            ) : loadErr ? (
              <div className="exercise-picker-loading">{loadErr}</div>
            ) : (
              <ExerciseListPanel
                density="compact"
                title="Filter:"
                exercises={exercises}
                muscleGroups={muscleGroups}
                filterIdx={filterIdx}
                onFilterIdxChange={setFilterIdx}
                showFavorites={false}
                markedIds={new Set(selectedExerciseId ? [selectedExerciseId] : [])}
                markedClassName="planned"
                onItemClick={(ex) => {
                  setSelectedExerciseId(ex.id);

                  setSetsValue(3);
                  setRepsValue(8);
                  setKgValue(20);

                  const { min, sec } = splitRestSeconds(120, 120);
                  setRestMinValue(min);
                  setRestSecValue(sec);

                  setView("details");
                }}
              />
            )}
          </div>
        </>
      ) : view === "details" ? (
        <>
          <div className="exercise-picker-title">
            {selectedExercise ? selectedExercise.name : "Exercise details"}
          </div>

          <div className="exercise-picker-details">
            <div className="muted" style={{ marginBottom: 10 }}>
              Selected: {setsValue} sets • {repsValue} reps • {kgValue} kg
            </div>

            <div className="exercise-picker-details-grid">
              <div className="exercise-picker-details-col">
                <div className="exercise-picker-details-label">Series</div>
                <RollingPicker
                  value={String(setsValue)}
                  options={setsOptions}
                  unit="sets"
                  {...bindIntPicker(setSetsValue, 3)}
                />
              </div>

              <div className="exercise-picker-details-col">
                <div className="exercise-picker-details-label">Reps</div>
                <RollingPicker
                  value={String(repsValue)}
                  options={repsOptions}
                  unit="reps"
                  {...bindIntPicker(setRepsValue, 8)}
                />
              </div>

              <div className="exercise-picker-details-col">
                <div className="exercise-picker-details-label">Kg</div>
                <RollingPicker
                  value={String(kgValue)}
                  options={kgOptions}
                  unit="kg"
                  {...bindFloatPicker(setKgValue, 0)}
                />
              </div>
            </div>

            <div className="exercise-picker-details-actions">
              <button
                type="button"
                className="btn"
                onClick={() => (mode === "edit" ? onClose?.() : setView("exercise"))}
              >
                {mode === "edit" ? "Cancel" : "Back"}
              </button>

              <button
                type="button"
                className="btn primary"
                onClick={goToRestStep}
                disabled={!selectedExercise || busy}
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="exercise-picker-title">Break time</div>

          <div className="exercise-picker-details">
            <div className="muted" style={{ marginBottom: 10 }}>
              After each set: {restMinValue} min • {restSecValue} sec
            </div>

            <div className="exercise-picker-details-grid is-rest">
              <div className="exercise-picker-details-col">
                <div className="exercise-picker-details-label">Minutes</div>
                <RollingPicker
                  value={String(restMinValue)}
                  options={restMinOptions}
                  unit="min"
                  {...bindIntPicker(setRestMinValue, 2)}
                />
              </div>

              <div className="exercise-picker-details-col">
                <div className="exercise-picker-details-label">Seconds</div>
                <RollingPicker
                  value={String(restSecValue)}
                  options={restSecOptions}
                  unit="sec"
                  {...bindIntPicker(setRestSecValue, 0)}
                />
              </div>
            </div>

            <div className="exercise-picker-details-actions">
              <button type="button" className="btn" onClick={() => setView("details")}>
                Back
              </button>

              <button
                type="button"
                className="btn primary"
                onClick={handleSaveAll}
                disabled={!selectedExercise || busy}
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}